import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "./firebase/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  EmailAuthProvider,
  linkWithCredential,
  updatePassword,
} from "firebase/auth";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const googleProvider = new GoogleAuthProvider();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Strong password validation
  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  // 1. Normal Email/Password Signup
  const signup = async (email, password) => {
    console.log("Signup called with:", email);
    
    if (!validatePassword(password)) {
      throw new Error("Password must be 8+ chars with uppercase, lowercase, number & special char");
    }

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      console.log("Signup successful:", result.user.email);
      return result.user;
    } catch (error) {
      console.error("Signup error:", error.code, error.message);
      throw error; // Let the component handle the specific error code
    }
  };

  // 2. Link password to existing Google account
  const linkPasswordToGoogleAccount = async (email, password) => {
    console.log("Linking password for:", email);
    
    try {
      // First, sign in with Google
      console.log("Requesting Google sign-in...");
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log("Google sign-in successful:", user.email);

      // Verify the email matches
      if (user.email.toLowerCase() !== email.toLowerCase()) {
        await signOut(auth);
        throw new Error(`Please sign in with the Google account linked to ${email}. You signed in with ${user.email}`);
      }

      // Create credential and link it
      const credential = EmailAuthProvider.credential(email, password);
      console.log("Linking credential...");
      await linkWithCredential(user, credential);
      
      console.log("Password linked successfully!");
      return user;
    } catch (error) {
      console.error("Link password error:", error.code, error.message);
      
      if (error.code === "auth/provider-already-linked") {
        throw new Error("This account already has a password set.");
      }
      if (error.code === "auth/credential-already-in-use") {
        throw new Error("This password is already used by another account.");
      }
      if (error.code === "auth/requires-recent-login") {
        throw new Error("Please sign out and sign in with Google again to set a password.");
      }
      throw error;
    }
  };

  const signin = (email, password) => {
    console.log("Signin called:", email);
    return signInWithEmailAndPassword(auth, email, password);
  };
  
  const signInWithGoogle = () => {
    console.log("Google sign-in called");
    return signInWithPopup(auth, googleProvider);
  };
  
  const logout = () => {
    console.log("Logout called");
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed:", user ? user.email : "No user");
      setCurrentUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    signin,
    signInWithGoogle,
    linkPasswordToGoogleAccount,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};